/**
 * Thrive Sports custom racket builder.
 *
 * The picker lives on the collection and product pages rather than inside one
 * section, so the in-progress build is kept in localStorage and read back by
 * whichever page needs it.
 */
(() => {
  const STORE_KEY = 'thrive_build';
  const PENDING_KEY = 'thrive_build_pending';
  const RETURN_KEY = 'thrive_build_return';

  const SLOTS = {
    blade: { label: 'Blade', noun: 'blade' },
    forehand: { label: 'Forehand rubber', noun: 'rubber' },
    backhand: { label: 'Backhand rubber', noun: 'rubber' },
  };

  /* localStorage throws in private mode and some embedded browsers, so every
     access is guarded and simply degrades to "no saved build". */
  const read = (key, fallback) => {
    try {
      const raw = window.localStorage.getItem(key);
      return raw ? JSON.parse(raw) : fallback;
    } catch (error) {
      return fallback;
    }
  };

  const write = (key, value) => {
    try {
      if (value === null) window.localStorage.removeItem(key);
      else window.localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      /* nothing we can do; the build just will not persist */
    }
  };

  const getBuild = () => read(STORE_KEY, {}) || {};

  /* Pending is {slot, source}. Older saves held just the slot string, so those
     are normalised rather than discarded. */
  const getPending = () => {
    const raw = read(PENDING_KEY, null);
    if (!raw) return null;
    if (typeof raw === 'string') return { slot: raw, source: '' };
    return raw.slot ? raw : null;
  };

  const money = (cents, currency) => {
    const amount = (Number(cents) || 0) / 100;
    try {
      return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: currency || 'INR',
        maximumFractionDigits: 0,
      }).format(amount);
    } catch (error) {
      return `₹${Math.round(amount)}`;
    }
  };

  /* ---------------------------------------------------------------- picking */

  const startSelection = (slot, destination) => {
    const base = (destination || '').split('?')[0];
    write(RETURN_KEY, window.location.pathname + window.location.hash);
    /* The token is what marks this as a build visit. Reaching the very same
       collection from the nav menu produces an identical path, so the state
       cannot be inferred from the URL alone. */
    window.location.href = `${base}?build=${encodeURIComponent(slot)}`;
  };

  const buildToken = () => {
    try {
      return new URLSearchParams(window.location.search).get('build');
    } catch (error) {
      return null;
    }
  };

  /**
   * Keeps the pending pick in step with how the shopper arrived.
   *
   * A collection page carrying the build token starts (or continues) the pick.
   * A collection page without it means they navigated here themselves, so the
   * pick is abandoned and cards return to Add to cart.
   *
   * Product pages are skipped on purpose: that is where a pick is confirmed,
   * and the token does not survive the click through from a card.
   */
  const syncPendingWithUrl = () => {
    const path = window.location.pathname;
    const onCollection = path.indexOf('/collections/') === 0 && path.indexOf('/products/') === -1;
    if (!onCollection) return;

    const slot = buildToken();
    if (slot && SLOTS[slot]) {
      write(PENDING_KEY, { slot: slot, source: path });
      return;
    }

    if (getPending()) {
      write(PENDING_KEY, null);
      write(RETURN_KEY, null);
    }
  };

  const cancelSelection = () => {
    const back = read(RETURN_KEY, null);
    write(PENDING_KEY, null);
    write(RETURN_KEY, null);
    if (back) window.location.href = back;
    else renderBanner();
  };

  const completeSelection = (slot, selection) => {
    const build = getBuild();
    build[slot] = selection;
    write(STORE_KEY, build);

    const back = read(RETURN_KEY, null) || '/collections/customized-rackets#build';
    write(PENDING_KEY, null);
    write(RETURN_KEY, null);
    window.location.href = back;
  };

  const clearSlot = (slot) => {
    const build = getBuild();
    delete build[slot];
    write(STORE_KEY, build);
    renderBuilder();
  };

  /* --------------------------------------------------------------- the bar */

  const renderBanner = () => {
    const existing = document.querySelector('.thrive-buildbar');
    const pending = getPending();

    const active = Boolean(pending && SLOTS[pending.slot]);

    /* Flags the page while a part is being chosen. Collection cards use this
       to drop their Add to cart, which would otherwise buy the component
       outright instead of adding it to the build. */
    document.body.classList.toggle('thrive-building', active);

    if (!active) {
      if (existing) existing.remove();
      return;
    }

    const config = SLOTS[pending.slot];
    const bar = existing || document.createElement('div');
    bar.className = 'thrive-buildbar';
    bar.innerHTML = `
      <span class="thrive-buildbar__text">
        Building your racket — choose your <strong>${config.label}</strong>
      </span>
      <button class="thrive-buildbar__cancel" type="button" data-build-cancel>Cancel</button>
    `;
    if (!existing) document.body.appendChild(bar);
  };

  /**
   * Pagination, sort and filter links rebuild the collection URL from scratch
   * and drop the build token, which would read as "browsed away" and end the
   * pick. Re-attaching it keeps a build alive while paging through rubbers.
   */
  const keepTokenOnLinks = () => {
    const pending = getPending();
    if (!pending) return;

    const path = window.location.pathname;
    if (path.indexOf('/collections/') !== 0 || path.indexOf('/products/') !== -1) return;

    document.querySelectorAll(`a[href^="${path}"]`).forEach((link) => {
      const href = link.getAttribute('href');
      if (!href || href.indexOf('build=') !== -1) return;
      if (href.indexOf('/products/') !== -1) return;

      const joiner = href.indexOf('?') === -1 ? '?' : '&';
      link.setAttribute('href', `${href}${joiner}build=${encodeURIComponent(pending.slot)}`);
    });
  };

  /* ----------------------------------------------------------- product page */

  const setupProductChooser = () => {
    const chooser = document.querySelector('[data-build-chooser]');
    if (!chooser) return;

    const pending = getPending();
    if (!pending || !SLOTS[pending.slot]) {
      chooser.hidden = true;
      return;
    }

    const config = SLOTS[pending.slot];
    chooser.hidden = false;

    const label = chooser.querySelector('[data-build-chooser-label]');
    if (label) label.textContent = `Use this ${config.noun} as your ${config.label.toLowerCase()}`;

    const button = chooser.querySelector('[data-build-chooser-button]');
    if (button) button.textContent = `Choose for ${config.label}`;
  };

  const readVariants = () => {
    const node = document.querySelector('[data-build-variants]');
    if (!node) return [];
    try {
      return JSON.parse(node.textContent) || [];
    } catch (error) {
      return [];
    }
  };

  const selectedVariantId = () => {
    const input = document.querySelector('form[action*="/cart/add"] input[name="id"]');
    return input ? Number(input.value) : null;
  };

  const chooseCurrentProduct = (chooser) => {
    const pending = getPending();
    if (!pending) return;

    const variants = readVariants();
    if (!variants.length) return;

    const id = selectedVariantId();
    const variant = variants.find((item) => Number(item.id) === id) || variants[0];
    if (!variant) return;

    completeSelection(pending.slot, {
      id: variant.id,
      title: chooser.dataset.productTitle || '',
      variantTitle: variant.title && variant.title.indexOf('Default') === -1 ? variant.title : '',
      price: variant.price,
      image: variant.image || chooser.dataset.productImage || '',
      url: chooser.dataset.productUrl || '',
    });
  };

  /* --------------------------------------------------------------- builder */

  const renderBuilder = () => {
    const root = document.querySelector('[data-build-root]');
    if (!root) return;

    const currency = root.dataset.currency || 'INR';
    const servicePrice = Number(root.dataset.servicePrice) || 0;
    const build = getBuild();
    let total = servicePrice;
    let complete = true;

    Object.keys(SLOTS).forEach((slot) => {
      const card = root.querySelector(`[data-build-card="${slot}"]`);
      if (!card) return;

      const selection = build[slot];
      const filled = Boolean(selection && selection.id);
      card.classList.toggle('is-filled', filled);

      const media = card.querySelector('[data-card-media]');
      const name = card.querySelector('[data-card-name]');
      const price = card.querySelector('[data-card-price]');
      const action = card.querySelector('[data-card-action]');
      const remove = card.querySelector('[data-card-remove]');

      if (filled) {
        total += Number(selection.price) || 0;
        if (media) {
          media.innerHTML = selection.image
            ? `<img src="${selection.image}" alt="${selection.title}" loading="lazy">`
            : '';
        }
        if (name) {
          name.textContent = selection.variantTitle
            ? `${selection.title} · ${selection.variantTitle}`
            : selection.title;
        }
        if (price) price.textContent = money(selection.price, currency);
        if (action) action.textContent = 'Change';
        if (remove) remove.hidden = false;
      } else {
        complete = false;
        if (media) media.innerHTML = '';
        if (name) name.textContent = '';
        if (price) price.textContent = '';
        if (action) action.textContent = `Choose ${SLOTS[slot].noun}`;
        if (remove) remove.hidden = true;
      }

      const summaryValue = root.querySelector(`[data-summary="${slot}"]`);
      if (summaryValue) {
        summaryValue.textContent = filled
          ? money(build[slot].price, currency)
          : 'Not selected';
      }
    });

    const totalEl = root.querySelector('[data-build-total]');
    if (totalEl) totalEl.textContent = money(total, currency);

    const submit = root.querySelector('[data-build-submit]');
    if (submit) {
      submit.disabled = !complete;
      submit.textContent = complete ? 'Add build to cart' : 'Pick all three to continue';
    }
  };

  const addBuildToCart = async (root) => {
    const build = getBuild();
    const slots = Object.keys(SLOTS);
    if (slots.some((slot) => !build[slot] || !build[slot].id)) return;

    const submit = root.querySelector('[data-build-submit]');
    const status = root.querySelector('[data-build-status]');
    const serviceVariant = root.dataset.serviceVariant;
    const buildId = `TS-${Date.now().toString(36).toUpperCase()}`;

    const items = slots.map((slot) => ({
      id: Number(build[slot].id),
      quantity: 1,
      properties: { 'Custom build': buildId, Part: SLOTS[slot].label },
    }));

    if (serviceVariant) {
      items.push({
        id: Number(serviceVariant),
        quantity: 1,
        properties: { 'Custom build': buildId, Part: 'Assembly service' },
      });
    }

    if (submit) {
      submit.disabled = true;
      submit.textContent = 'Adding…';
    }
    if (status) status.textContent = '';

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.description || error.message || 'Could not add this build.');
      }

      write(STORE_KEY, null);
      window.location.href = '/cart';
    } catch (error) {
      if (status) status.textContent = error.message;
      renderBuilder();
    }
  };

  /* ------------------------------------------------------------------ cart */

  /**
   * Shopify has no native way to price a made-to-order bundle, so a build is
   * four real line items sharing a "Custom build" property. Here those rows are
   * collapsed behind one summary row so the cart reads as a single product.
   */
  const PART_ORDER = ['Blade', 'Forehand rubber', 'Backhand rubber', 'Assembly service'];

  const groupCartBuilds = async () => {
    const table = document.querySelector('.cart-items__table, .cart-items');
    if (!table) return;

    let cart;
    try {
      const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      cart = await response.json();
    } catch (error) {
      return;
    }

    if (!cart || !Array.isArray(cart.items)) return;

    document.querySelectorAll('.thrive-bundle').forEach((node) => node.remove());
    document
      .querySelectorAll('[data-thrive-member]')
      .forEach((row) => row.removeAttribute('data-thrive-member'));

    const groups = new Map();
    cart.items.forEach((item) => {
      const props = item.properties || {};
      const id = props['Custom build'];
      if (!id) return;
      if (!groups.has(id)) groups.set(id, []);
      groups.get(id).push(item);
    });

    if (!groups.size) return;

    const currency = cart.currency || 'INR';

    groups.forEach((items, buildId) => {
      const rows = items
        .map((item) => document.querySelector(`[data-key="${CSS.escape(item.key)}"]`))
        .filter(Boolean);

      if (!rows.length) return;

      const total = items.reduce((sum, item) => sum + (item.final_line_price || 0), 0);
      const sorted = [...items].sort(
        (a, b) =>
          PART_ORDER.indexOf((a.properties || {}).Part) -
          PART_ORDER.indexOf((b.properties || {}).Part)
      );
      const cover = sorted.find((item) => item.image) || sorted[0];

      const breakdown = sorted
        .map((item) => {
          const part = (item.properties || {}).Part || 'Part';
          return `<li><span>${part}</span><span>${item.product_title}</span><span>${money(
            item.final_line_price,
            currency
          )}</span></li>`;
        })
        .join('');

      const summary = document.createElement('tr');
      summary.className = 'thrive-bundle';
      summary.innerHTML = `
        <td class="thrive-bundle__cell" colspan="4">
          <button class="thrive-bundle__head" type="button" aria-expanded="false" data-bundle-toggle>
            <span class="thrive-bundle__media">
              ${cover && cover.image ? `<img src="${cover.image}" alt="">` : ''}
            </span>
            <span class="thrive-bundle__info">
              <span class="thrive-bundle__title">Custom racket build</span>
              <span class="thrive-bundle__meta">${items.length} parts · ${buildId}</span>
            </span>
            <span class="thrive-bundle__price">${money(total, currency)}</span>
            <span class="thrive-bundle__chevron" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                   stroke-linecap="round" stroke-linejoin="round"><path d="m6 9 6 6 6-6"/></svg>
            </span>
          </button>
          <ul class="thrive-bundle__list" hidden>${breakdown}</ul>
          <p class="thrive-bundle__hint" hidden>
            Expand the rows below to change or remove individual parts.
          </p>
        </td>
      `;

      rows[0].parentNode.insertBefore(summary, rows[0]);

      rows.forEach((row) => {
        row.setAttribute('data-thrive-member', buildId);
        row.hidden = true;
      });

      const toggle = summary.querySelector('[data-bundle-toggle]');
      toggle.addEventListener('click', () => {
        const open = toggle.getAttribute('aria-expanded') === 'true';
        toggle.setAttribute('aria-expanded', open ? 'false' : 'true');
        summary.classList.toggle('is-open', !open);
        summary.querySelector('.thrive-bundle__list').hidden = open;
        summary.querySelector('.thrive-bundle__hint').hidden = open;
        rows.forEach((row) => {
          row.hidden = open;
        });
      });
    });

    if (!document.querySelector('.thrive-bundle-again')) {
      const again = document.createElement('a');
      again.className = 'button thrive-bundle-again';
      again.href = '/collections/customized-rackets#build';
      again.textContent = 'Build another racket';
      const anchor = table.closest('.cart-items__wrapper') || table.parentNode;
      if (anchor) anchor.appendChild(again);
    }
  };

  /* ---------------------------------------------------------------- events */

  document.addEventListener('click', (event) => {
    const choose = event.target.closest('[data-build-choose]');
    if (choose) {
      event.preventDefault();
      startSelection(choose.dataset.buildChoose, choose.dataset.buildDestination);
      return;
    }

    const remove = event.target.closest('[data-card-remove]');
    if (remove) {
      event.preventDefault();
      clearSlot(remove.dataset.cardRemove);
      return;
    }

    if (event.target.closest('[data-build-cancel]')) {
      event.preventDefault();
      cancelSelection();
      return;
    }

    const chooserButton = event.target.closest('[data-build-chooser-button]');
    if (chooserButton) {
      event.preventDefault();
      const chooser = chooserButton.closest('[data-build-chooser]');
      if (chooser) chooseCurrentProduct(chooser);
      return;
    }

    const submit = event.target.closest('[data-build-submit]');
    if (submit) {
      event.preventDefault();
      const root = submit.closest('[data-build-root]');
      if (root) addBuildToCart(root);
    }
  });

  let regroupTimer = null;
  const scheduleRegroup = () => {
    clearTimeout(regroupTimer);
    regroupTimer = setTimeout(groupCartBuilds, 120);
  };

  /* Horizon re-renders cart rows after every quantity or removal change, which
     wipes the injected summary — so the grouping is rebuilt when rows change. */
  const watchCart = () => {
    const container = document.querySelector('.cart-items__table, .cart-items');
    if (!container || container.dataset.thriveWatched) return;
    container.dataset.thriveWatched = 'true';

    new MutationObserver((records) => {
      const structural = records.some((record) =>
        [...record.addedNodes, ...record.removedNodes].some(
          (node) => node.nodeType === 1 && !node.classList.contains('thrive-bundle')
        )
      );
      if (structural) scheduleRegroup();
    }).observe(container, { childList: true, subtree: true });
  };

  /**
   * Header items that only exist to open a dropdown.
   *
   * Shopify requires every menu item to have a URL, so "Shop by Category" and
   * "Shop by Skill" point at #categories / #skill. Those go nowhere, and a
   * click would otherwise jump the page, so navigation is cancelled and the
   * theme's own disclosure toggle is used instead.
   */
  const NON_NAV = ['#categories', '#skill', '#accessories'];

  document.addEventListener(
    'click',
    (event) => {
      const link = event.target.closest('a[href]');
      if (!link) return;

      const href = link.getAttribute('href') || '';
      if (!NON_NAV.some((token) => href.endsWith(token))) return;

      event.preventDefault();

      const item = link.closest('.menu-list__list-item, li');
      const toggle = item && item.querySelector('.menu-list__disclosure');
      if (toggle) toggle.click();
    },
    true
  );

  const init = () => {
    syncPendingWithUrl();
    renderBanner();
    keepTokenOnLinks();
    setupProductChooser();
    renderBuilder();
    groupCartBuilds();
    watchCart();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
  document.addEventListener('cart:refresh', scheduleRegroup);
  document.addEventListener('cart:update', scheduleRegroup);
})();
